/**
 * Validação de documentos brasileiros (CPF/CNPJ) — funções puras (#109/#111).
 *
 * Usadas no cadastro de condomínio (CNPJ) e do responsável (CPF) para impedir
 * o avanço com documento inválido. Sem I/O — fáceis de testar e reutilizar no
 * Worker (api) e, futuramente, no cliente.
 */

/** Mantém apenas os dígitos de uma string. */
export function digitsOnly(value: string): string {
  return (value ?? '').replace(/\D+/g, '');
}

/** Verdadeiro quando todos os caracteres são iguais (ex.: "00000000000"). */
function allSame(s: string): boolean {
  return s.length > 0 && /^(\d)\1*$/.test(s);
}

/** Calcula um dígito verificador pela soma ponderada (módulo 11). */
function dvModulo11(base: string, pesos: readonly number[]): number {
  let soma = 0;
  for (let i = 0; i < pesos.length; i++) {
    // biome-ignore lint/style/noNonNullAssertion: índices delimitados por pesos.length
    soma += Number(base[i]!) * pesos[i]!;
  }
  const resto = soma % 11;
  return resto < 2 ? 0 : 11 - resto;
}

const CNPJ_PESOS_1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const;
const CNPJ_PESOS_2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const;

/** Valida um CNPJ pelos dígitos verificadores (aceita com ou sem máscara). */
export function isValidCnpj(cnpj: string): boolean {
  const d = digitsOnly(cnpj);
  if (d.length !== 14 || allSame(d)) return false;
  const dv1 = dvModulo11(d.slice(0, 12), CNPJ_PESOS_1);
  if (dv1 !== Number(d[12])) return false;
  const dv2 = dvModulo11(d.slice(0, 13), CNPJ_PESOS_2);
  return dv2 === Number(d[13]);
}

const CPF_PESOS_1 = [10, 9, 8, 7, 6, 5, 4, 3, 2] as const;
const CPF_PESOS_2 = [11, 10, 9, 8, 7, 6, 5, 4, 3, 2] as const;

/** Valida um CPF pelos dígitos verificadores (aceita com ou sem máscara). */
export function isValidCpf(cpf: string): boolean {
  const d = digitsOnly(cpf);
  if (d.length !== 11 || allSame(d)) return false;
  const dv1 = dvModulo11(d.slice(0, 9), CPF_PESOS_1);
  if (dv1 !== Number(d[9])) return false;
  const dv2 = dvModulo11(d.slice(0, 10), CPF_PESOS_2);
  return dv2 === Number(d[10]);
}

/** Formata um CNPJ (14 dígitos) como `00.000.000/0000-00`; senão devolve o original. */
export function formatCnpj(cnpj: string): string {
  const d = digitsOnly(cnpj);
  if (d.length !== 14) return cnpj;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/** Formata um CPF (11 dígitos) como `000.000.000-00`; senão devolve o original. */
export function formatCpf(cpf: string): string {
  const d = digitsOnly(cpf);
  if (d.length !== 11) return cpf;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

/**
 * Mascara um CPF/CNPJ para exibição (LGPD): preserva apenas um trecho central.
 * CPF expõe 3 dígitos centrais; CNPJ expõe 4 dígitos do número base. Documento
 * desconhecido vira `***` (nunca expõe os dígitos completos).
 */
export function mascararDocumento(doc: string): string {
  const d = digitsOnly(doc);
  if (d.length === 11) return `***.***.${d.slice(6, 9)}-**`;
  if (d.length === 14) return `**.***.***/${d.slice(8, 12)}-**`;
  return '***';
}
